// IND-027: «Rotación de materiales» del Panel. Cálculos puros sobre el
// resumen del backend (mismas cifras que el detalle del material) y la fila
// de la proyección (stock hoy): cobertura, parados, rotación lenta, valores
// sugeridos de los umbrales y vista previa de a cuántos materiales afectan.
import type {
  MaterialRotationRow,
  MaterialRotationSummary,
  MaterialRotationThresholds,
} from '../services/industrialCostService';

// Selector de la sección: materias primas en kg y embalajes en unidades.
export type RotationGroup = 'kg' | 'embalajes';
export const ROTATION_GROUPS: Array<{ key: RotationGroup; label: string }> = [
  { key: 'kg', label: 'Materias primas' },
  { key: 'embalajes', label: 'Embalajes (unidades)' },
];

// Sin datos suficientes, estos (los de la tarea).
export const DEFAULT_THRESHOLDS: MaterialRotationThresholds = {
  idle_weeks: 8,
  slow_rotation_days: 60,
  // IND-030: el plazo de entrega de la planta (20 días laborables).
  short_coverage_days: 20,
  rhythm_weeks: 8,
  abc_a_pct: 80,
  abc_b_pct: 95,
};

const MIN_VALUES_FOR_SUGGESTION = 5;

export type RotationTableRow = MaterialRotationRow & {
  stock: number | null;
  // null con stock negativo o sin stock calculado («—»).
  coverage_days: number | null;
  no_consumption: boolean;
  // Rotación que se muestra: «—» con stock negativo o sin stock.
  rotation_shown: number | null;
  idle: boolean;
  slow: boolean;
  short: boolean;
};

export function daysBetween(from: string, to: string): number {
  const start = new Date(`${from.slice(0, 10)}T12:00:00`).getTime();
  const end = new Date(`${to.slice(0, 10)}T12:00:00`).getTime();
  return Math.round((end - start) / 86_400_000);
}

/** Percentil por rango más cercano, como el backend. */
export function percentile(values: number[], pct: number): number | null {
  const ordered = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (ordered.length === 0) return null;
  const rank = Math.max(1, Math.ceil((ordered.length * pct) / 100));
  return ordered[rank - 1];
}

/** Ritmo de las N últimas semanas: su consumo ÷ sus días laborables. */
export function perWorkdayForWeeks(weekly: number[], weekWorkdays: number[], weeks: number): number | null {
  const consumed = weekly.slice(-weeks).reduce((total, value) => total + value, 0);
  const workdays = weekWorkdays.slice(-weeks).reduce((total, value) => total + value, 0);
  return workdays > 0 ? consumed / workdays : null;
}

export function coverageDays(stock: number | null, perWorkday: number | null): { value: number | null; noConsumption: boolean } {
  if (stock === null || !Number.isFinite(stock) || stock < 0) return { value: null, noConsumption: false };
  if (perWorkday === null || perWorkday <= 0) return { value: null, noConsumption: true };
  return { value: stock / perWorkday, noConsumption: false };
}

/** Parado: stock > 0 y sin consumo en las N últimas semanas. */
export function isIdle(stock: number | null, lastConsumption: string | null, today: string, idleWeeks: number): boolean {
  if (stock === null || !(stock > 0)) return false;
  if (!lastConsumption) return true;
  return daysBetween(lastConsumption, today) > idleWeeks * 7;
}

/** Por qué la rotación de la tabla sale «—» (null si tiene cifra). */
function ddmm(value: string): string {
  const [, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}`;
}

/** Motivo con fechas de una rotación sin cifra (el mismo en la tabla y en el
 *  detalle): días negativos, albaranes que faltan, recuento corto. */
export function rotationGapText(period: {
  too_many_negative: boolean;
  negative_days_pct: number | null;
  negative_from: string | null;
  negative_to: string | null;
  entries_from: string | null;
  computed_from: string | null;
  has_base: boolean;
  missing_days?: number | null;
  // Fin del periodo (si los albaranes empiezan después, no hay datos).
  to?: string | null;
}): string {
  if (period.too_many_negative) {
    const range =
      period.negative_from && period.negative_to
        ? `stock negativo del ${ddmm(period.negative_from)} al ${ddmm(period.negative_to)}, `
        : 'stock negativo el ';
    // Con la regla del 29/10 estos negativos ya no son de albaranes que faltan.
    return `${range}${Math.round(period.negative_days_pct ?? 0)} % de los días`;
  }
  if (period.computed_from) {
    // IND-031: el periodo empieza en el primer recuento desde entradas completas.
    const missing = period.missing_days
      ? period.missing_days === 1
        ? 'falta 1 día para el mínimo'
        : `faltan ${period.missing_days} días para el mínimo`
      : 'menos de 4 semanas';
    return period.entries_from
      ? `entradas completas desde ${ddmm(period.entries_from)}; primer recuento válido ${ddmm(period.computed_from)}; ${missing}`
      : `hay recuento desde ${ddmm(period.computed_from)}; ${missing}`;
  }
  if (period.entries_from && !period.has_base) {
    // Sin día usable por las entradas, no por el recuento.
    return period.to && period.entries_from.slice(0, 10) > period.to.slice(0, 10)
      ? `sin entradas completas en el periodo (empiezan el ${ddmm(period.entries_from)})`
      : `entradas completas desde ${ddmm(period.entries_from)}; aún sin recuento válido`;
  }
  if (!period.has_base) return 'sin recuento en el periodo';
  return 'sin consumo en el periodo';
}

export function rotationDashReason(row: RotationTableRow): string | null {
  if (row.rotation_shown !== null) return null;
  if (row.stock === null) return 'sin stock calculado';
  const gap = rotationGapText({
    too_many_negative: row.rotation_too_many_negative,
    negative_days_pct: row.rotation_negative_days_pct,
    negative_from: row.rotation_negative_from,
    negative_to: row.rotation_negative_to,
    entries_from: row.rotation_entries_from,
    computed_from: row.rotation_computed_from,
    has_base: row.rotation_has_base,
    missing_days: row.rotation_missing_days,
  });
  return gap;
}

export function buildRotationRows({
  rows,
  stockByMaterial,
  today,
  thresholds,
}: {
  rows: MaterialRotationRow[];
  stockByMaterial: ReadonlyMap<string, number | null>;
  today: string;
  thresholds: MaterialRotationThresholds;
}): RotationTableRow[] {
  return rows.map((row) => {
    const stock = stockByMaterial.has(row.material_id) ? (stockByMaterial.get(row.material_id) ?? null) : null;
    const coverage = coverageDays(stock, row.per_workday);
    // Decisión 29/10: el stock negativo solo hoy no impide la rotación del
    // periodo (la cobertura sí sale «—»).
    const rotationShown = stock !== null ? row.rotation_days : null;
    return {
      ...row,
      stock,
      coverage_days: coverage.value,
      no_consumption: coverage.noConsumption,
      rotation_shown: rotationShown,
      idle: isIdle(stock, row.last_consumption_date, today, thresholds.idle_weeks),
      slow: rotationShown !== null && rotationShown > thresholds.slow_rotation_days,
      short: coverage.value !== null && coverage.value < thresholds.short_coverage_days,
    };
  });
}

/** Clases con otros cortes (vista previa), con la misma regla que el backend. */
export function abcClassesForCuts(rows: MaterialRotationRow[], aPct: number, bPct: number): Map<string, 'A' | 'B' | 'C'> {
  const out = new Map<string, 'A' | 'B' | 'C'>();
  const byGroup = new Map<string, MaterialRotationRow[]>();
  for (const row of rows) {
    if (!row.group || !(row.consumed_12m_qty > 0)) continue;
    byGroup.set(row.group, [...(byGroup.get(row.group) ?? []), row]);
  }
  for (const items of byGroup.values()) {
    const total = items.reduce((sum, row) => sum + row.consumed_12m_qty, 0);
    let cumulative = 0;
    for (const row of [...items].sort(
      (a, b) => b.consumed_12m_qty - a.consumed_12m_qty || a.material_id.localeCompare(b.material_id),
    )) {
      const before = (cumulative / total) * 100;
      cumulative += row.consumed_12m_qty;
      out.set(row.material_id, before < aPct ? 'A' : before < bPct ? 'B' : 'C');
    }
  }
  return out;
}

export type ThresholdSuggestion = { value: number; reason: string };

const qtyFormat = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });

/** Valores sugeridos por los datos de la empresa (reglas fijas de la tarea). */
export function suggestThresholds({
  summary,
  stockByMaterial,
}: {
  summary: Pick<MaterialRotationSummary, 'rows' | 'consumption_gap_p90_days'>;
  stockByMaterial: ReadonlyMap<string, number | null>;
}): Record<keyof MaterialRotationThresholds, ThresholdSuggestion> {
  const rotations: number[] = [];
  const coverages: number[] = [];
  for (const row of summary.rows) {
    const stock = stockByMaterial.get(row.material_id) ?? null;
    if (stock === null) continue;
    if (row.rotation_days !== null) rotations.push(row.rotation_days);
    if (stock < 0) continue;
    const coverage = coverageDays(stock, row.per_workday).value;
    if (coverage !== null && stock > 0) coverages.push(coverage);
  }

  // IND-030: el valor por defecto es de negocio (DEFAULT_THRESHOLDS); el
  // cálculo por percentiles queda como referencia («según tus datos: …»).
  const rotationP85 = rotations.length >= MIN_VALUES_FOR_SUGGESTION ? percentile(rotations, 85) : null;
  const slow: ThresholdSuggestion = {
    value: DEFAULT_THRESHOLDS.slow_rotation_days,
    reason:
      rotationP85 !== null
        ? `según tus datos: el 85 % de los materiales rota en ${qtyFormat.format(rotationP85)} días o menos`
        : 'según tus datos: aún no hay suficientes materiales con rotación',
  };

  const gap = summary.consumption_gap_p90_days;
  const idle: ThresholdSuggestion = {
    value: DEFAULT_THRESHOLDS.idle_weeks,
    reason:
      gap !== null && gap !== undefined
        ? `según tus datos: el 90 % de los intervalos entre dos consumos es de ${qtyFormat.format(gap)} días o menos`
        : 'según tus datos: aún no hay consumos suficientes',
  };

  const coverageP20 = coverages.length >= MIN_VALUES_FOR_SUGGESTION ? percentile(coverages, 20) : null;
  const short: ThresholdSuggestion = {
    value: DEFAULT_THRESHOLDS.short_coverage_days,
    reason:
      'el plazo de entrega de la planta es de 20 días laborables' +
      (coverageP20 !== null
        ? `; según tus datos: el 20 % de los materiales con stock cubre ${qtyFormat.format(coverageP20)} días o menos`
        : ''),
  };

  return {
    idle_weeks: idle,
    slow_rotation_days: slow,
    short_coverage_days: short,
    rhythm_weeks: { value: 8, reason: 'las 8 semanas del ritmo habitual del detalle del material' },
    abc_a_pct: { value: 80, reason: 'el corte habitual del análisis ABC' },
    abc_b_pct: { value: 95, reason: 'el corte habitual del análisis ABC' },
  };
}

export function suggestedValues(
  suggestions: Record<keyof MaterialRotationThresholds, ThresholdSuggestion>,
): MaterialRotationThresholds {
  return {
    idle_weeks: suggestions.idle_weeks.value,
    slow_rotation_days: suggestions.slow_rotation_days.value,
    short_coverage_days: suggestions.short_coverage_days.value,
    rhythm_weeks: suggestions.rhythm_weeks.value,
    abc_a_pct: suggestions.abc_a_pct.value,
    abc_b_pct: suggestions.abc_b_pct.value,
  };
}

/** Vista previa: a cuántos materiales del grupo afecta cada valor escrito. */
export function previewCounts({
  summary,
  groupRows,
  stockByMaterial,
  today,
  values,
}: {
  summary: Pick<MaterialRotationSummary, 'rows' | 'week_workdays'>;
  groupRows: MaterialRotationRow[];
  stockByMaterial: ReadonlyMap<string, number | null>;
  today: string;
  values: MaterialRotationThresholds;
}): Record<keyof MaterialRotationThresholds, number> {
  const stockOf = (row: MaterialRotationRow) => stockByMaterial.get(row.material_id) ?? null;
  const classes = abcClassesForCuts(summary.rows, values.abc_a_pct, values.abc_b_pct);
  return {
    idle_weeks: groupRows.filter((row) => isIdle(stockOf(row), row.last_consumption_date, today, values.idle_weeks)).length,
    slow_rotation_days: groupRows.filter((row) => {
      const stock = stockOf(row);
      return stock !== null && row.rotation_days !== null && row.rotation_days > values.slow_rotation_days;
    }).length,
    short_coverage_days: groupRows.filter((row) => {
      const coverage = coverageDays(stockOf(row), row.per_workday).value;
      return coverage !== null && coverage < values.short_coverage_days;
    }).length,
    // Con ese ritmo, cuántos quedan con cobertura corta.
    rhythm_weeks: groupRows.filter((row) => {
      const rate = perWorkdayForWeeks(row.weekly_consumed, summary.week_workdays, values.rhythm_weeks);
      const coverage = coverageDays(stockOf(row), rate).value;
      return coverage !== null && coverage < values.short_coverage_days;
    }).length,
    abc_a_pct: groupRows.filter((row) => classes.get(row.material_id) === 'A').length,
    abc_b_pct: groupRows.filter((row) => classes.get(row.material_id) === 'B').length,
  };
}
